import { z } from 'zod'

// =====================
// One bulk protocol for every module.
//
// The rule that governs the shape: a bulk mutation must NEVER silently skip a
// record the user believes was changed. So the response is not "the rows that
// worked" — it is a full accounting of every id the caller sent:
//
//   requested = succeeded + failed          (always, no exceptions)
//
// Ids that fall outside the caller's organization, that were already deleted,
// or whose per-record update threw are reported as FAILURES with a reason —
// never quietly dropped from the result. The caller can then show "8 updated,
// 2 couldn't be changed" instead of lying about 10.
//
// Callers should treat a 200 with failures as a partial success, not an error;
// only an entirely-invalid request (no ids, too many ids) is a 4xx.
// =====================

// Hard ceiling on ids per bulk request. Bulk work runs per-record (each module's
// single-record update carries its own side effects: realtime, audit, custom
// fields, lifecycle hooks), so an unbounded list is a request-timeout waiting to
// happen. Matches the existing tickets bulk cap.
export const BULK_OPERATION_MAX_IDS = 200

// How many per-record updates run concurrently inside one bulk request. Small
// enough to keep the connection pool healthy under several concurrent bulk
// callers; large enough that 200 records don't run strictly serially.
export const BULK_OPERATION_CONCURRENCY = 10

export interface BulkOperationFailure {
  id: string
  // Human-readable, surfaced directly in the UI ("Not found in this
  // organization", "Stage 'negotiation' is not valid for this deal", ...).
  reason: string
}

export interface BulkOperationResult {
  // Distinct ids the server actually considered (post-dedupe).
  requested: number
  succeeded: number
  failed: number
  // The ids that were genuinely changed — safe to use for optimistic cache patching.
  succeededIds: string[]
  failures: BulkOperationFailure[]
}

export const BulkOperationFailureSchema = z.object({
  id: z.string(),
  reason: z.string(),
})

export const BulkOperationResultSchema = z.object({
  requested: z.number().int().nonnegative(),
  succeeded: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  succeededIds: z.array(z.string()),
  failures: z.array(BulkOperationFailureSchema),
})

// Convenience for the client: a one-line summary of a bulk result.
export const summarizeBulkResult = (
  result: BulkOperationResult,
  noun: string,
  nounPlural?: string,
): string => {
  const plural = nounPlural ?? `${noun}s`
  const label = (n: number) => (n === 1 ? noun : plural)
  if (result.failed === 0) {
    return `Updated ${result.succeeded} ${label(result.succeeded)}`
  }
  if (result.succeeded === 0) {
    return `Couldn't update ${result.failed} ${label(result.failed)}`
  }
  return `Updated ${result.succeeded} ${label(result.succeeded)}; ${result.failed} couldn't be changed`
}

// ---------------------------------------------------------------------------
// Bulk tag assignment (module-agnostic, mirrors the per-record tag endpoints).
// ---------------------------------------------------------------------------

// 'add'     — union the given tags onto whatever each record already has
// 'remove'  — take the given tags off each record (no-op where not present)
// 'replace' — the given tags become the record's complete tag set
export const BulkTagModeSchema = z.enum(['add', 'remove', 'replace'])
export type BulkTagMode = z.infer<typeof BulkTagModeSchema>

export const BulkAssignModuleTagsSchema = z.object({
  moduleType: z.string().min(1),
  moduleIds: z.array(z.string().uuid()).min(1).max(BULK_OPERATION_MAX_IDS),
  tagIds: z.array(z.string().uuid()),
  mode: BulkTagModeSchema,
})
export type BulkAssignModuleTags = z.infer<typeof BulkAssignModuleTagsSchema>

// ---------------------------------------------------------------------------
// Per-module bulk-update payloads.
//
// Deliberately an ALLOWLIST, not the module's full Update DTO. Bulk editing is
// for the fields an agency changes across many records at once — reassigning a
// departing member's book, re-staging a pipeline, re-pointing contacts at a
// renamed client. Identity fields (name, email, phone, code) are excluded on
// purpose: setting fifty contacts' email to one value is never what anyone
// meant, and a bulk endpoint that allows it is a footgun with an audit trail.
// ---------------------------------------------------------------------------

// `null` clears the field; omitted means "leave alone".
export const BulkUpdateContactsSchema = z
  .object({
    assignedToId: z.string().uuid().nullable().optional(),
    lifecycleStage: z.string().max(30).optional(),
    vendorStage: z.string().max(30).optional(),
    entityId: z.string().uuid().nullable().optional(),
    supplierId: z.string().uuid().nullable().optional(),
    source: z.string().max(255).nullable().optional(),
    industry: z.string().max(100).nullable().optional(),
    department: z.string().nullable().optional(),
  })
  .refine(value => Object.keys(value).length > 0, {
    message: 'No fields to update',
  })
export type BulkUpdateContacts = z.infer<typeof BulkUpdateContactsSchema>

export const BulkUpdateDealsSchema = z
  .object({
    assignedToId: z.string().uuid().nullable().optional(),
    stage: z.string().max(50).optional(),
    forecastCategory: z.string().max(20).optional(),
    closeDate: z.string().nullable().optional(),
    expectedStartDate: z.string().nullable().optional(),
    entityId: z.string().uuid().nullable().optional(),
    primaryContactId: z.string().uuid().nullable().optional(),
    probability: z.number().int().min(0).max(100).nullable().optional(),
  })
  .refine(value => Object.keys(value).length > 0, {
    message: 'No fields to update',
  })
export type BulkUpdateDeals = z.infer<typeof BulkUpdateDealsSchema>

export const BulkUpdateSuppliersSchema = z
  .object({
    lifecycleStage: z.string().max(30).optional(),
    isActive: z.boolean().optional(),
    isPreferred: z.boolean().optional(),
    paymentTermCode: z.string().max(50).nullable().optional(),
    currency: z.string().length(3).optional(),
  })
  .refine(value => Object.keys(value).length > 0, {
    message: 'No fields to update',
  })
export type BulkUpdateSuppliers = z.infer<typeof BulkUpdateSuppliersSchema>

export interface BulkTagResult extends BulkOperationResult {
  // Assignment rows created / deleted across the whole batch — lets the UI say
  // "added 2 tags to 15 contacts" rather than just counting records.
  assignmentsAdded: number
  assignmentsRemoved: number
}
